/**
 * The client register — the list.
 *
 * The first screen in EQ Practice that shows CLIENTS rather than obligations.
 * Until this existed the register could hold 178 clients and the product would
 * show nothing, because both data pages render obligation periods.
 *
 * Search covers the names, the client number and every statutory number, so
 * "who is 4900000123" is answerable by typing it. Filters live in the URL, as
 * they do on the other two pages, so a view can be sent to someone.
 *
 * R13 (the Company List standard): sortable headings, VAT and year-end
 * filters, a column picker, CSV and print. Sort and filters are in the URL
 * too, and sorting never drops a filter. The column picker is a HIDE-list --
 * the columns you turned off -- kept in the URL as well, so a shared link
 * shows the recipient the same columns.
 */

import { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { api, type ClientListResponse, type ClientStatus } from '../api'
import { AppShell } from '../components/AppShell'
import { EmptyState, ErrorState, LoadingState } from '../components/States'
import type { AuthState, ObligationTypeInUse } from '../api'
import { vatTerm } from '../lib/vatTerms'

const ENTITY_LABELS: Record<string, string> = {
  individual: 'Individual',
  company: 'Company',
  cc: 'CC',
  trust: 'Trust',
  partnership: 'Partnership',
  deceased_estate: 'Deceased estate',
  sole_proprietor: 'Sole proprietor',
  npo: 'NPO',
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** Optional columns. Number and name are always shown. */
const COLUMNS = [
  ['type', 'Type'],
  ['group', 'Group'],
  ['vat', 'VAT'],
  ['yearEnd', 'Year-end'],
  ['registrations', 'Registered for'],
  ['status', 'Status'],
  ['changed', 'Last changed'],
] as const
type ColumnKey = (typeof COLUMNS)[number][0]

const STATUS_CLASSES: Record<ClientStatus, string> = {
  active: 'bg-eq-success-bg text-eq-success',
  dormant: 'bg-eq-grey text-eq-neutral',
  resigned: 'bg-eq-danger-bg text-eq-danger',
}

export function ClientsPage({
  obligationTypes,
  auth,
}: {
  obligationTypes: ObligationTypeInUse[]
  auth: AuthState
}) {
  const [params, setParams] = useSearchParams()
  const q = params.get('q') ?? ''
  const status = params.get('status')
  const entityType = params.get('entityType')
  const vat = params.get('vat')
  const yearEnd = params.get('yearEnd')
  const sort = params.get('sort')
  const dir = params.get('dir')
  const hidden = new Set((params.get('hide') ?? '').split(',').filter(Boolean))
  const shows = (c: ColumnKey) => !hidden.has(c)
  const [picking, setPicking] = useState(false)

  const [draft, setDraft] = useState(q)
  const [data, setData] = useState<ClientListResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Keep the box in step when the URL changes underneath (back button, or a
  // link someone was sent).
  useEffect(() => setDraft(q), [q])

  const load = useCallback(async () => {
    try {
      setError(null)
      setData(await api.clients({ q, status, entityType, vat, yearEnd, sort, dir }))
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [q, status, entityType, vat, yearEnd, sort, dir])

  /** Click a heading: sort by it; click again: reverse. Filters are kept. */
  const sortBy = (key: string) => {
    const next = new URLSearchParams(params)
    const current = sort ?? 'clientNumber'
    next.set('sort', key)
    next.set('dir', current === key && (dir ?? 'asc') === 'asc' ? 'desc' : 'asc')
    setParams(next, { replace: true })
  }
  const arrow = (key: string) =>
    (sort ?? 'clientNumber') === key ? ((dir ?? 'asc') === 'asc' ? ' ▲' : ' ▼') : ''
  const toggleColumn = (key: ColumnKey) => {
    const nextHidden = new Set(hidden)
    if (nextHidden.has(key)) nextHidden.delete(key)
    else nextHidden.add(key)
    setParam('hide', [...nextHidden].join(','))
  }

  useEffect(() => {
    void load()
  }, [load])

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params)
    if (value === null || value === '') next.delete(key)
    else next.set(key, value)
    setParams(next, { replace: true })
  }

  return (
    <AppShell
      obligationTypes={obligationTypes}
      auth={auth}
      eyebrow="Practice"
      title="Clients"
      action={
        <span className="flex items-center gap-2">
          <Link
            to="/clients/import"
            className="rounded-eq-sm border border-eq-navy bg-white px-4 py-2 font-heading text-[13px] font-bold text-eq-navy"
          >
            Import clients
          </Link>
          <Link
            to="/clients/new"
            className="rounded-eq-sm bg-eq-navy px-4 py-2 font-heading text-[13px] font-bold text-white"
          >
            New client
          </Link>
        </span>
      }
      subtitle={
        data === null
          ? undefined
          : `${data.counts.active} active, ${data.counts.dormant} dormant, ` +
            `${data.counts.resigned} resigned. Practice owns the client register — ` +
            'Accounts and Payroll read it from here.'
      }
    >
      <form
        className="mb-4 flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          setParam('q', draft.trim())
        }}
      >
        <input
          type="search"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Name, client number, or any tax number"
          aria-label="Search clients"
          className="w-[22rem] rounded-eq-sm border border-eq-border px-3 py-2 text-[14px]"
        />
        <button
          type="submit"
          className="rounded-eq-sm bg-eq-navy px-4 py-2 font-heading text-[13px] font-bold text-white"
        >
          Search
        </button>
        <select
          value={vat ?? ''}
          onChange={(e) => setParam('vat', e.target.value)}
          aria-label={`Filter by ${vatTerm('VAT_TERM_VAT_REGISTRATION_STATUS')}`}
          className="rounded-eq-sm border border-eq-border bg-white px-2 py-2 text-[13px]"
        >
          <option value="">VAT: any</option>
          <option value="registered">{vatTerm('VAT_TERM_REGISTERED_FOR_VAT')}</option>
          <option value="not_registered">{vatTerm('VAT_TERM_NOT_REGISTERED_FOR_VAT')}</option>
          <option value="unknown">{`${vatTerm('VAT_TERM_VAT_REGISTRATION_STATUS')} not captured`}</option>
        </select>
        <select
          value={yearEnd ?? ''}
          onChange={(e) => setParam('yearEnd', e.target.value)}
          aria-label="Filter by financial year-end"
          className="rounded-eq-sm border border-eq-border bg-white px-2 py-2 text-[13px]"
        >
          <option value="">Year-end: any</option>
          {MONTHS.map((m, i) => (
            <option key={m} value={String(i + 1)}>
              Year-end: {m}
            </option>
          ))}
        </select>
        {(q !== '' || status !== null || entityType !== null || vat !== null || yearEnd !== null) && (
          <button
            type="button"
            onClick={() => {
              // Clear the FILTERS. Sort order and hidden columns are a view
              // preference, not a filter, and stay as they were.
              const next = new URLSearchParams()
              for (const k of ['sort', 'dir', 'hide']) {
                const v = params.get(k)
                if (v !== null) next.set(k, v)
              }
              setParams(next, { replace: true })
            }}
            className="text-[12.5px] text-eq-helper underline"
          >
            Clear
          </button>
        )}

        <span className="ml-auto flex items-center gap-2">
          <Filter label="All" active={status === null} onClick={() => setParam('status', null)} />
          <Filter
            label={`Active${data ? ` (${data.counts.active})` : ''}`}
            active={status === 'active'}
            onClick={() => setParam('status', 'active')}
          />
          <Filter
            label={`Dormant${data ? ` (${data.counts.dormant})` : ''}`}
            active={status === 'dormant'}
            onClick={() => setParam('status', 'dormant')}
          />
          <Filter
            label={`Resigned${data ? ` (${data.counts.resigned})` : ''}`}
            active={status === 'resigned'}
            onClick={() => setParam('status', 'resigned')}
          />
        </span>
      </form>

      <div className="mb-3 flex flex-wrap items-center gap-3 print:hidden">
        <div className="relative">
          <button
            type="button"
            onClick={() => setPicking((v) => !v)}
            aria-expanded={picking}
            className="rounded-eq-sm border border-eq-border bg-white px-3 py-1.5 text-[13px] font-semibold text-eq-neutral"
          >
            Columns
          </button>
          {picking && (
            <div className="absolute z-10 mt-1 flex w-48 flex-col gap-1 rounded-eq-sm border border-eq-border bg-white p-2 shadow-eq-raised">
              {COLUMNS.map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-[13px]">
                  <input type="checkbox" checked={shows(key)} onChange={() => toggleColumn(key)} className="h-4 w-4" />
                  {label}
                </label>
              ))}
            </div>
          )}
        </div>
        <a
          href={api.clientsCsvUrl({ q, status, entityType, vat, yearEnd, sort, dir })}
          className="rounded-eq-sm border border-eq-border bg-white px-3 py-1.5 text-[13px] font-semibold text-eq-neutral"
        >
          Export CSV
        </a>
        <button
          type="button"
          onClick={() => window.print()}
          className="rounded-eq-sm border border-eq-border bg-white px-3 py-1.5 text-[13px] font-semibold text-eq-neutral"
        >
          Print
        </button>
        <span className="text-[12px] text-eq-helper">CSV opens in Excel, with the filters and order shown here.</span>
      </div>

      {error !== null && <ErrorState message={error} />}
      {error === null && data === null && <LoadingState what="clients" />}
      {data !== null && data.clients.length === 0 && (
        <EmptyState>
          {q === '' && status === null
            ? 'The register is empty. Add a client with New client, or load a register with the import (prisma/import-clients.ts).'
            : 'No client matches that.'}
        </EmptyState>
      )}

      {data !== null && data.clients.length > 0 && (
        <div className="overflow-x-auto rounded-eq-md border border-eq-border bg-white">
          <table className="w-full border-collapse text-[14px]">
            <thead>
              <tr className="border-b border-eq-border text-left">
                <Th className="w-[5rem] text-right">
                  <SortButton onClick={() => sortBy('clientNumber')}>No.{arrow('clientNumber')}</SortButton>
                </Th>
                <Th>
                  <SortButton onClick={() => sortBy('shortName')}>Client{arrow('shortName')}</SortButton>
                </Th>
                {shows('type') && (
                  <Th className="w-[9rem]">
                    <SortButton onClick={() => sortBy('entityType')}>Type{arrow('entityType')}</SortButton>
                  </Th>
                )}
                {shows('group') && <Th className="w-[11rem]">Group</Th>}
                {shows('vat') && (
                  <Th className="w-[10rem]">
                    <SortButton onClick={() => sortBy('vatCategoryCode')}>VAT{arrow('vatCategoryCode')}</SortButton>
                  </Th>
                )}
                {shows('yearEnd') && (
                  <Th className="w-[6rem]">
                    <SortButton onClick={() => sortBy('financialYearEndMonth')}>Year-end{arrow('financialYearEndMonth')}</SortButton>
                  </Th>
                )}
                {shows('registrations') && <Th className="w-[12rem]">Registered for</Th>}
                {shows('status') && (
                  <Th className="w-[7rem]">
                    <SortButton onClick={() => sortBy('status')}>Status{arrow('status')}</SortButton>
                  </Th>
                )}
                {shows('changed') && <Th className="w-[8rem]">Last changed</Th>}
              </tr>
            </thead>
            <tbody>
              {data.clients.map((c) => (
                <tr key={c.id} className="border-b border-eq-border last:border-0 hover:bg-eq-grey">
                  <td className="px-3 py-2 text-right tabular-nums text-eq-neutral">
                    {c.clientNumber}
                  </td>
                  <td className="px-3 py-2">
                    <Link
                      to={`/clients/${c.id}`}
                      className="font-semibold text-eq-navy underline-offset-2 hover:underline"
                    >
                      {c.shortName}
                    </Link>
                    {c.registeredName !== null && c.registeredName !== c.shortName && (
                      <div className="text-[12.5px] text-eq-helper">{c.registeredName}</div>
                    )}
                  </td>
                  {shows('type') && (
                  <td className="px-3 py-2 text-eq-neutral">
                    {c.entityType === null ? (
                      <span className="text-eq-helper">Not set</span>
                    ) : (
                      ENTITY_LABELS[c.entityType] ?? c.entityType
                    )}
                  </td>
                  )}
                  {shows('group') && (
                  <td className="px-3 py-2 text-eq-neutral">
                    {c.groupName === null ? (
                      <span className="text-eq-helper">—</span>
                    ) : (
                      <span title={c.groupNumber ?? undefined}>{c.groupName}</span>
                    )}
                  </td>
                  )}
                  {shows('vat') && (
                    <td className="px-3 py-2 text-eq-neutral">
                      {c.vatRegistered === null ? (
                        <span className="text-eq-helper">Not captured</span>
                      ) : c.vatRegistered ? (
                        <>
                          {c.vatCategoryCode === null ? vatTerm('VAT_TERM_REGISTERED_FOR_VAT') : `Category ${c.vatCategoryCode}`}
                          {c.vatNumber !== null && <div className="text-[12.5px] tabular-nums text-eq-helper">{c.vatNumber}</div>}
                        </>
                      ) : (
                        vatTerm('VAT_TERM_NOT_REGISTERED_FOR_VAT')
                      )}
                    </td>
                  )}
                  {shows('yearEnd') && (
                    <td className="px-3 py-2 text-eq-neutral">
                      {c.financialYearEndMonth === null ? <span className="text-eq-helper">—</span> : MONTHS[c.financialYearEndMonth - 1]}
                    </td>
                  )}
                  {shows('registrations') && (
                  <td className="px-3 py-2">
                    {c.registrations.length === 0 ? (
                      <span className="text-eq-helper">—</span>
                    ) : (
                      <span className="flex flex-wrap gap-1">
                        {c.registrations.map((r) => (
                          <span
                            key={r}
                            className="rounded-eq-pill bg-eq-grey px-2 py-0.5 font-heading text-[11px] font-bold tracking-[.04em] text-eq-neutral"
                          >
                            {r}
                          </span>
                        ))}
                      </span>
                    )}
                  </td>
                  )}
                  {shows('status') && (
                  <td className="px-3 py-2">
                    <span
                      className={[
                        'rounded-eq-pill px-2 py-0.5 font-heading text-[11px] font-bold uppercase tracking-[.06em]',
                        STATUS_CLASSES[c.status],
                      ].join(' ')}
                    >
                      {c.status}
                    </span>
                  </td>
                  )}
                  {shows('changed') && (
                    <td className="px-3 py-2 tabular-nums text-eq-neutral">
                      {c.lastChangedAt === null ? <span className="text-eq-helper">—</span> : c.lastChangedAt.slice(0, 10)}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data !== null && data.clients.length > 0 && (
        <p className="mt-3 text-[12.5px] text-eq-helper">
          Showing {data.clients.length} of {data.counts.active + data.counts.dormant + data.counts.resigned}.
        </p>
      )}
    </AppShell>
  )
}

function Th({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      className={`px-3 py-2 font-heading text-[11px] font-bold uppercase tracking-[.08em] text-eq-neutral ${className}`}
    >
      {children}
    </th>
  )
}

function Filter({
  label,
  active,
  onClick,
}: {
  label: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'rounded-eq-pill px-3 py-1.5 font-heading text-[12px] font-bold',
        active ? 'bg-eq-navy text-white' : 'bg-white text-eq-neutral border border-eq-border',
      ].join(' ')}
    >
      {label}
    </button>
  )
}

/** A heading that sorts. A real button, so the keyboard can reach it. */
function SortButton({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="font-heading text-[11px] font-bold uppercase tracking-[.08em] text-eq-neutral hover:text-eq-navy"
    >
      {children}
    </button>
  )
}
